import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Send, Save, MailCheck, AlertTriangle, Copy, Users, User, Building2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KIND_LABELS = {
  create: { label: "New ticket", desc: "Fires when a ticket is created — from an inbound email or manual create." },
  reply: { label: "Response to ticket", desc: "Fires when a support analyst posts a non-private reply/update on a ticket." },
  customer_reply: { label: "Customer replied by email", desc: "Fires when a customer emails a reply that lands back on an existing ticket (matched by [TKT-####] in the subject). Also auto-flips the ticket from 'Waiting on Customer' to 'In Progress' and reopens closed tickets." },
  status_change: { label: "Status change", desc: "Fires when a ticket's status changes (excluding the move to Resolved/Closed, which uses the Resolved trigger)." },
  close: { label: "Resolved / closed", desc: "Fires the first time a ticket moves to Resolved or Closed." },
  sla_warning: { label: "SLA breaching (pre-deadline warning)", desc: "Fires once, a configurable lead time before a ticket's SLA resolution deadline. Set the lead time and working hours under Support config → SLA. Typically sent to the owner + a manager (not the customer)." },
  sla_breach: { label: "SLA breached", desc: "Fires once when a ticket passes its SLA resolution deadline. Typically sent to the owner + a manager." },
};

const VAR_DESC = {
  ticket_number: "Sequential ticket number, e.g. 1042",
  ticket_id: "Internal ticket id",
  subject: "Ticket subject line",
  account_name: "Customer account name",
  owner_name: "Employee who owns the ticket",
  status: "Current ticket status",
  priority: "Ticket priority",
  ticket_description: "Original ticket description / body",
  resolution_notes: "Resolution notes (resolved trigger)",
  contact_first_name: "First name of the person who raised it",
  contact_full_name: "Full name of the person who raised it",
  portal_link: "Deep link to open the ticket",
  author_name: "Name of the analyst who posted the reply",
  comment_body: "The reply / update text (response trigger)",
  old_status: "Previous status (status change trigger)",
  new_status: "New status (status change trigger)",
  sla_due: "SLA resolution deadline, formatted (SLA triggers)",
  minutes_to_breach: "Minutes remaining until the SLA deadline (SLA warning)",
};

export default function TicketNotificationsPanel() {
  const [cfg, setCfg] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null); // `${kind}:${audience}`
  const [testTo, setTestTo] = useState("");
  const [openUserPicker, setOpenUserPicker] = useState({}); // kind -> bool
  const [noReply, setNoReply] = useState([]);
  const [noReplyDefaults, setNoReplyDefaults] = useState([]);
  const [noReplyInput, setNoReplyInput] = useState("");
  const [noReplySaving, setNoReplySaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/ticket-notifications/config");
      setCfg(r.data);
      setDrafts(r.data.templates || {});
      setUsers(r.data.users || []);
    } catch (e) {
      toast.error("Could not load ticket notification config");
    }
    try {
      const nr = await api.get("/admin/ticket-notifications/no-reply-senders");
      setNoReply(nr.data.items || []);
      setNoReplyDefaults(nr.data.defaults || []);
    } catch (e) {
      // non-fatal
    }
  };
  useEffect(() => { load(); }, []);

  const addNoReply = () => {
    const v = (noReplyInput || "").trim().toLowerCase();
    if (!v) return;
    const ok = (v.split("@").length === 2) || v.startsWith("@");
    if (!ok) return toast.error("Use a full address, '@domain.com' or 'local@*'");
    if (noReply.includes(v)) { setNoReplyInput(""); return; }
    setNoReply([...noReply, v]);
    setNoReplyInput("");
  };
  const removeNoReply = (v) => setNoReply(noReply.filter((x) => x !== v));
  const resetNoReplyDefaults = () => setNoReply([...noReplyDefaults]);
  const saveNoReply = async () => {
    setNoReplySaving(true);
    try {
      const r = await api.put("/admin/ticket-notifications/no-reply-senders", { items: noReply });
      setNoReply(r.data.items || []);
      toast.success("No-reply senders saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setNoReplySaving(false); }
  };

  const setField = (kind, field, value) => {
    setDrafts((d) => ({ ...d, [kind]: { ...d[kind], [field]: value } }));
  };
  const setRecipient = (kind, key, value) => {
    setDrafts((d) => ({
      ...d,
      [kind]: { ...d[kind], recipients: { ...(d[kind].recipients || {}), [key]: value } },
    }));
  };
  const toggleExtraUser = (kind, userId) => {
    setDrafts((d) => {
      const cur = d[kind].recipients || {};
      const list = new Set(cur.extra_user_ids || []);
      if (list.has(userId)) list.delete(userId); else list.add(userId);
      return { ...d, [kind]: { ...d[kind], recipients: { ...cur, extra_user_ids: Array.from(list) } } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/ticket-notifications/config", { templates: drafts });
      toast.success("Alert settings saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const testSend = async (kind, audience) => {
    if (!testTo.trim()) return toast.error("Enter an email address to send the preview to");
    setTesting(`${kind}:${audience}`);
    try {
      await api.post("/admin/ticket-notifications/test-send", { kind, audience, to: testTo.trim() });
      toast.success(`Preview (${audience}) sent to ${testTo}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally { setTesting(null); }
  };

  const copyVar = (v) => {
    navigator.clipboard.writeText(`{{${v}}}`);
    toast.success(`{{${v}}} copied — paste into a subject or body`);
  };

  if (!cfg) return null;

  return (
    <Card className="border-slate-200" data-testid="ticket-notifications-panel">
      <CardHeader className="border-b border-slate-100 py-4 flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <MailCheck className="h-4 w-4 text-blue-600" />
          Ticket alerts &amp; email templates
          <span className={`ml-2 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${
            cfg.m365_configured ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-amber-50 text-amber-800 border border-amber-200"
          }`}>
            {cfg.m365_configured ? `Sends via Microsoft 365 · ${cfg.sender_mailbox}` : "M365 not configured"}
          </span>
        </CardTitle>
        <div className="flex items-center gap-2">
          <Input
            placeholder="preview email address"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="h-8 w-56 text-xs"
            data-testid="ticket-notif-test-email-input"
          />
          <Button size="sm" onClick={save} disabled={saving} data-testid="ticket-notif-save-btn">
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save all"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-4 text-sm">
        {!cfg.m365_configured && (
          <div className="rounded border border-amber-200 bg-amber-50/50 p-3 text-amber-900 text-xs flex items-start gap-2" data-testid="ticket-notif-m365-warning">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              All ticket alerts are sent through Microsoft Graph from your support mailbox. Configure the M365 integration above first — and make sure the Azure app has the <span className="font-mono">Mail.Send</span> application permission granted (in addition to Mail.Read/Mail.ReadWrite). See "Enable outbound email" at the bottom.
            </div>
          </div>
        )}

        <div className="rounded-md bg-blue-50/60 border border-blue-100 p-3 text-xs text-blue-900">
          Every ticket email — to both the customer who raised it and your internal team — is now sent from <span className="font-mono">{cfg.sender_mailbox || "your support mailbox"}</span> via Microsoft 365. Configure below which people are alerted for each trigger and edit the exact wording.
        </div>

        {/* No-reply / loop protection */}
        <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 space-y-3" data-testid="no-reply-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-heading text-slate-900 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" /> Loop protection — no-reply / notification-only senders
              </div>
              <p className="text-xs text-slate-600 mt-1 max-w-3xl">
                Some partner systems (e.g. <span className="font-mono">soc@barracuda.com</span>) auto-reply to every email we send them, which creates a ticket loop. Xentra will STILL create/update the ticket from their emails — it just won't email them back a "we've opened your ticket" reply.
                Add full addresses (<span className="font-mono">soc@barracuda.com</span>), whole domains (<span className="font-mono">@barracuda.com</span>) or local-part wildcards (<span className="font-mono">noreply@*</span>).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={resetNoReplyDefaults} data-testid="no-reply-reset" className="text-xs">Reset to defaults</Button>
              <Button size="sm" onClick={saveNoReply} disabled={noReplySaving} data-testid="no-reply-save">
                <Save className="h-3.5 w-3.5 mr-1" /> {noReplySaving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="soc@barracuda.com  ·  @partner.co.uk  ·  noreply@*"
              value={noReplyInput}
              onChange={(e) => setNoReplyInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNoReply(); } }}
              className="h-8 text-xs"
              data-testid="no-reply-input"
            />
            <Button size="sm" variant="secondary" onClick={addNoReply} data-testid="no-reply-add" className="text-xs">Add</Button>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="no-reply-chips">
            {noReply.length === 0 && (
              <span className="text-[11px] text-slate-500 italic">No patterns configured — customer alerts will be sent for every sender.</span>
            )}
            {noReply.map((v) => (
              <span key={v} className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded border border-amber-300 bg-white text-slate-800" data-testid={`no-reply-chip-${v}`}>
                {v}
                <button
                  type="button"
                  onClick={() => removeNoReply(v)}
                  className="text-slate-400 hover:text-rose-600"
                  data-testid={`no-reply-remove-${v}`}
                  aria-label={`Remove ${v}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Variable reference */}
        <details className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid="ticket-notif-variables">
          <summary className="cursor-pointer font-medium text-slate-900 text-xs flex items-center gap-2">
            <Copy className="h-3.5 w-3.5 text-slate-500" /> Ticket variables — click to copy into any subject or body
          </summary>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(cfg.available_variables || []).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => copyVar(v)}
                className="text-[11px] font-mono px-2 py-0.5 rounded border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-colors flex items-center gap-1"
                data-testid={`ticket-notif-var-${v}`}
                title={VAR_DESC[v] || v}
              >
                {`{{${v}}}`}<Copy className="h-2.5 w-2.5 opacity-40" />
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">These map to the live ticket object at send time. Missing values render blank. Hover a chip to see what it inserts.</p>
        </details>

        {(cfg.available_kinds || Object.keys(drafts)).map((kind) => {
          const meta = KIND_LABELS[kind] || { label: kind, desc: "" };
          const d = drafts[kind] || { enabled: true, recipients: {}, customer_subject: "", customer_body: "", staff_subject: "", staff_body: "" };
          const rec = d.recipients || {};
          const extraCount = (rec.extra_user_ids || []).length;
          return (
            <div key={kind} className="border border-slate-200 rounded-lg p-4 space-y-4" data-testid={`ticket-notif-tmpl-${kind}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{meta.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{meta.desc}</div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Switch
                    checked={!!d.enabled}
                    onCheckedChange={(v) => setField(kind, "enabled", v)}
                    data-testid={`ticket-notif-toggle-${kind}`}
                  />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{d.enabled ? "On" : "Off"}</span>
                </div>
              </div>

              {/* Recipients */}
              <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3 space-y-2">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Who gets this alert</div>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                    <Checkbox
                      checked={!!rec.customer}
                      onCheckedChange={(v) => setRecipient(kind, "customer", !!v)}
                      data-testid={`ticket-notif-rec-customer-${kind}`}
                    />
                    <Building2 className="h-3.5 w-3.5 text-slate-400" /> Customer who raised the ticket
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                    <Checkbox
                      checked={!!rec.owner}
                      onCheckedChange={(v) => setRecipient(kind, "owner", !!v)}
                      data-testid={`ticket-notif-rec-owner-${kind}`}
                    />
                    <User className="h-3.5 w-3.5 text-slate-400" /> Employee who owns the ticket
                  </label>
                  <button
                    type="button"
                    onClick={() => setOpenUserPicker((p) => ({ ...p, [kind]: !p[kind] }))}
                    className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-blue-700"
                    data-testid={`ticket-notif-extra-toggle-${kind}`}
                  >
                    <Users className="h-3.5 w-3.5 text-slate-400" /> Also notify team members
                    {extraCount > 0 && <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">{extraCount}</Badge>}
                    <ChevronDown className={`h-3 w-3 transition-transform ${openUserPicker[kind] ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {openUserPicker[kind] && (
                  <div className="mt-1 max-h-40 overflow-y-auto rounded border border-slate-200 bg-white p-2 space-y-1" data-testid={`ticket-notif-extra-list-${kind}`}>
                    {users.length === 0 && <div className="text-xs text-slate-400 px-1 py-2">No team members found.</div>}
                    {users.map((u) => (
                      <label key={u.user_id} className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 px-1 py-0.5 rounded hover:bg-slate-50">
                        <Checkbox
                          checked={(rec.extra_user_ids || []).includes(u.user_id)}
                          onCheckedChange={() => toggleExtraUser(kind, u.user_id)}
                          data-testid={`ticket-notif-extra-user-${kind}-${u.user_id}`}
                        />
                        <span className="font-medium">{u.name || u.email}</span>
                        <span className="text-slate-400">{u.email}</span>
                        {u.role && <span className="ml-auto text-[10px] font-mono uppercase text-slate-400">{u.role}</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Templates */}
              <Tabs defaultValue="customer" className="w-full">
                <TabsList className="h-8">
                  <TabsTrigger value="customer" className="text-xs" data-testid={`ticket-notif-tab-customer-${kind}`}>Customer email</TabsTrigger>
                  <TabsTrigger value="staff" className="text-xs" data-testid={`ticket-notif-tab-staff-${kind}`}>Internal team email</TabsTrigger>
                </TabsList>

                <TabsContent value="customer" className="mt-3 space-y-3">
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Subject</label>
                    <Input
                      value={d.customer_subject || ""}
                      onChange={(e) => setField(kind, "customer_subject", e.target.value)}
                      data-testid={`ticket-notif-customer-subject-${kind}`}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Body</label>
                    <Textarea
                      value={d.customer_body || ""}
                      onChange={(e) => setField(kind, "customer_body", e.target.value)}
                      rows={8}
                      data-testid={`ticket-notif-customer-body-${kind}`}
                      className="mt-1 font-mono text-[12px] leading-5"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" variant="outline" onClick={() => testSend(kind, "customer")}
                      disabled={!cfg.m365_configured || testing === `${kind}:customer` || !testTo}
                      data-testid={`ticket-notif-test-customer-${kind}`}>
                      <Send className="h-3.5 w-3.5 mr-1" /> {testing === `${kind}:customer` ? "Sending…" : "Send customer preview"}
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="staff" className="mt-3 space-y-3">
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Subject</label>
                    <Input
                      value={d.staff_subject || ""}
                      onChange={(e) => setField(kind, "staff_subject", e.target.value)}
                      data-testid={`ticket-notif-staff-subject-${kind}`}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Body</label>
                    <Textarea
                      value={d.staff_body || ""}
                      onChange={(e) => setField(kind, "staff_body", e.target.value)}
                      rows={8}
                      data-testid={`ticket-notif-staff-body-${kind}`}
                      className="mt-1 font-mono text-[12px] leading-5"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button size="sm" variant="outline" onClick={() => testSend(kind, "staff")}
                      disabled={!cfg.m365_configured || testing === `${kind}:staff` || !testTo}
                      data-testid={`ticket-notif-test-staff-${kind}`}>
                      <Send className="h-3.5 w-3.5 mr-1" /> {testing === `${kind}:staff` ? "Sending…" : "Send internal preview"}
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          );
        })}

        <details className="border-t border-slate-100 pt-3 text-xs text-slate-700">
          <summary className="cursor-pointer font-medium text-slate-900">Enable outbound email — Azure `Mail.Send` permission</summary>
          <div className="mt-2 space-y-2 pl-1">
            <p>Your Azure app needs the following application permissions (all with admin consent) so the support mailbox can read, mark-read and send:</p>
            <ol className="list-decimal ml-5 space-y-1">
              <li>Azure Portal → App registrations → your app → <b>API permissions → Add a permission</b>.</li>
              <li>Microsoft Graph → <b>Application permissions</b> → add <span className="font-mono">Mail.ReadWrite</span> and <span className="font-mono">Mail.Send</span>.</li>
              <li>Click <b>Grant admin consent</b>. All permissions should show a green tick.</li>
              <li>Send a preview above to confirm. If it fails with 403, wait ~5 min for propagation and retry.</li>
            </ol>
            <p className="text-slate-500">If you set up an Exchange Application Access Policy, it also gates <span className="font-mono">Mail.Send</span> — the group must contain <span className="font-mono">{cfg.sender_mailbox || "support@…"}</span> for outbound to succeed.</p>
          </div>
        </details>
      </CardContent>
    </Card>
  );
}
